import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  type Signal,
} from '@angular/core';
import {
  takeUntilDestroyed,
  toObservable,
  toSignal,
} from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { Currency } from '@principle-theorem/accounting';
import { combineLatest, merge, type Observable, Subject } from 'rxjs';
import { map, startWith, switchMap } from 'rxjs/operators';
import { DepositTemplateConfigDialogComponent } from './deposit-template-config-dialog.component';
import { DepositTemplateRowSummary } from './deposit-template-row-summary';
import { type IDepositTreatmentTemplateRow } from './deposit-treatment-template-rows';

interface IDepositTemplateRowState {
  readonly required: boolean;
  readonly invalid: boolean;
  readonly summary: string;
}

@Component({
  selector: 'pr-deposit-template-row',
  templateUrl: './deposit-template-row.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule],
})
export class DepositTemplateRowComponent {
  private readonly _dialog = inject(MatDialog);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _refresh$ = new Subject<void>();
  private readonly _row$: Observable<IDepositTreatmentTemplateRow>;

  readonly row = input.required<IDepositTreatmentTemplateRow>();
  readonly currency = input<Currency>(Currency.AUD);
  readonly state: Signal<IDepositTemplateRowState | undefined>;

  constructor() {
    this._row$ = toObservable(this.row);
    this.state = toSignal(
      combineLatest([this._row$, toObservable(this.currency)]).pipe(
        switchMap(([row, currency]) =>
          merge(
            row.form.valueChanges,
            row.form.statusChanges,
            this._refresh$
          ).pipe(
            startWith(undefined),
            map(
              (): IDepositTemplateRowState => ({
                required: !!row.form.controls.depositRequired.value,
                invalid: row.form.invalid,
                summary: DepositTemplateRowSummary.summary(row.form, currency),
              })
            )
          )
        )
      )
    );
  }

  openDialog(): void {
    this._dialog
      .open(DepositTemplateConfigDialogComponent, {
        data: { row: this.row() },
        width: '600px',
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => this._refresh$.next());
  }
}
